import { ChangeEvent, useEffect, useState } from "react";
import "../../page/style/style.css";
import { getUserDetails, updateUserInfo } from "../../services/User_service";
import LoaderAnnonce from "../../components/LoaderAnnonce";
import { ToastContainer, toast } from "react-toastify";
import { isValidRcs } from "../../utils/functions/validateRCSNumber";
import Swal from "sweetalert2";
import { AxiosError } from "axios";
import axios from "../../api/axios";
import { Button, Card, Divider, Tag, Space } from "antd";
import useAppContext from "../../hooks/useAppContext";
import { AppContextType } from "../../context/AppContext";
import RibModal from "../../page/client/avoir/RibModal";
import React from "react";
import {
  UserOutlined,
  BankOutlined,
  HomeOutlined,
  BuildOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined
} from "@ant-design/icons";

interface InputWithTooltipProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  tooltipMessage: string;
  readOnly: boolean;
  type?: string;
  className?: string;
  icon?: React.ReactNode;
}

interface GestionCompteAdminProps {
  userId?: number
  onClose?: () => void
}


const InputWithTooltip = ({
  id,
  label,
  value,
  onChange,
  tooltipMessage,
  readOnly,
  type = "text",
  icon
}: InputWithTooltipProps) => {
  const [localTooltip, setLocalTooltip] = useState<boolean>(false);

  const handleClick = () => {
    if (readOnly) {
      setLocalTooltip(true);
      setTimeout(() => {
        setLocalTooltip(false);
      }, 1700);
    }
  };

  return (
    <div className="input-container" style={{ position: "relative", width: "100%" }}>
      <label className="form-label" style={{ 
        fontWeight: 500, 
        marginBottom: "6px",
        color: "#555",
        fontSize: "clamp(12px, 2.5vw, 13px)",
        display: "block"
      }}>
        {icon && <span style={{ marginRight: "8px" }}>{icon}</span>}
        {label}
      </label>
      <div style={{ position: "relative", width: "100%" }}>
        <input
          type={type}
          id={id}
          className="form-control"
          value={value}
          onChange={onChange}
          onClick={handleClick}
          readOnly={readOnly}
          style={{
            padding: "10px 12px",
            borderRadius: "6px",
            border: readOnly ? "1px solid #e0e0e0" : "1px solid #d9d9d9",
            backgroundColor: readOnly ? "#fafafa" : "white",
            fontSize: "clamp(13px, 2vw, 14px)",
            transition: "all 0.3s",
            paddingLeft: icon ? "40px" : "12px",
            color: readOnly ? "#666" : "#333",
            width: "100%",
            boxSizing: "border-box"
          }}
        />
        {icon && (
          <span style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#999",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            {icon}
          </span>
        )}
      </div>
      {readOnly && localTooltip && (
        <div
          className="custom-tooltip"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            padding: "8px 12px",
            backgroundColor: "#2A919D",
            color: "#fff",
            borderRadius: "4px",
            zIndex: 1000,
            marginTop: "5px",
            fontSize: "clamp(11px, 1.8vw, 12px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            maxWidth: "100%",
            wordWrap: "break-word",
            whiteSpace: "normal",
            overflow: "visible"
          }}
        >
          {tooltipMessage}
        </div>
      )}
    </div>
  );
};

const PROFESSION_OPTIONS: Record<string, string[]> = {
  "Professions juridiques": [
    "Administrateur / Mandataire judiciaire",
    "Avocat",
    "Clerc / Formaliste de notaire",
    "Greffier / Assistant de greffe",
    "Huissier de justice / Commissaire de justice",
    "Juriste d'entreprise",
    "Notaire",
  ],
  "Professions comptables": [
    "Cabinet comptable / Conseil en gestion",
    "Commissaire aux comptes",
    "Expert-comptable",
    "Gestionnaire de paie / RAF",
  ],
  "Créateurs et dirigeants d'entreprise": [
    "Dirigeant d'entreprise",
    "Entrepreneur individuel / Auto-entrepreneur",
    "Office manager / Responsable administratif",
  ],
  "Intermédiaires et formalistes": [
    "Agence de communication / Publicité",
    "Cabinet de formalités juridiques",
    "Formaliste en annonces légales",
    "Secrétaire juridique indépendant",
    "Société de domiciliation",
  ],
  "Autres professions": [
    "Administration / Assistance administrative",
    "Ressources humaines",
    "Responsable juridique",
    "Consultant indépendant",
  ],
  "Autre": ["Autre (à préciser)"],
};

function GestionCompteAdmin({ userId, onClose }: GestionCompteAdminProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [showTooltip, setShowTooltip] = useState<boolean>(true);
  const [ribModalVisible, setRibModalVisible] = useState<boolean>(false);

  const [user, setUser] = useState({
    id: "",
    name: "",
    firstname: "",
    email: "",
    reference: "",
    civility: "",
    code_parrainage: "",
    adresse: "",
    number: "",
    country: "",
    postal_code: "",
    createdAt: "",
    raison_social: "",
    code_SIREN: "",
    code_NAF: "",
    tv_intra: "",
    user_type: "",
    status: "",
    profession: "",
    profession_other: "" as string | null,
    profession_is_other: false,
    is_professional: false,
    bank_name: "",
    account_holder: "",
    account_number: "",
    bic_swift: "",
  });

  const [initialUser, setInitialUser] = useState(user);
  const [updatedInformations, setUpdatedInformations] = useState<
    Record<string, any>
  >({});
  const [selectedProfessionCategory, setSelectedProfessionCategory] =
    useState<string>("");

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const { setUserName, setUserFirstname } = useAppContext() as AppContextType;

  // Récupérer les données de l'admin
useEffect(() => {
  const fetchUserData = async () => {
    setLoading(true);

    const userData = userId
      ? await getUserDetails(userId)
      : await getUserDetails();

    if (userData) {
      const isOther = !!userData.profession_is_other;

      const formattedUser = {
        id: userData.id?.toString() || "",
        name: userData.name || "",
        firstname: userData.firstname || "",
        email: userData.email || "",
        reference: userData.reference || "",
        civility: userData.civility || "",
        code_parrainage: userData.code_parrainage || "",
        adresse: userData.adresse || "",
        number: userData.number || "",
        country: userData.country || "",
        postal_code: userData.postal_code || "",
        createdAt: userData.createdAt
          ? new Date(userData.createdAt).toLocaleDateString()
          : "",
        raison_social: userData.raison_social || "",
        code_SIREN: userData.code_SIREN || "",
        code_NAF: userData.code_NAF || "",
        tv_intra: userData.tv_intra || "",
        user_type: userData.user_type || "",
        status: userData.status || "",
        profession: isOther ? "Autre (à préciser)" : userData.profession || "",
        profession_other: userData.profession_other ?? "",
        profession_is_other: isOther,
        is_professional:
          userData.user_type === "professionnel" ||
          userData.user_type === "entreprise",
        bank_name: userData.bank_name || "",
        account_holder: userData.account_holder || "",
        account_number: userData.account_number || "",
        bic_swift: userData.bic_swift || "",
      };

      setUser(formattedUser);
      setInitialUser(formattedUser);

      if (!userId) {
        // ⚠️ uniquement pour l'admin connecté
        setUserName(formattedUser.name);
        setUserFirstname(formattedUser.firstname);
      }
    }

    setLoading(false);
  };

  fetchUserData();
}, [userId, setUserName, setUserFirstname]);


  useEffect(() => {
    setHasChanges(Object.keys(updatedInformations).length > 0);
  }, [updatedInformations]);

  const handleEditClick = () => {
    setShowTooltip(false);
  };

  const handleCancel = () => {
    setUser(initialUser);
    setUpdatedInformations({});
    setShowTooltip(true);
  };

  // Gestion du SIREN
  const handleRcsInput = async (event: ChangeEvent<HTMLInputElement>) => {
    if (showTooltip) return;

    const rcs = event.target.value;
    setUser({ ...user, code_SIREN: rcs });

    if (rcs !== initialUser.code_SIREN) {
      setUpdatedInformations({ ...updatedInformations, code_SIREN: rcs });
    } else {
      const updatedInfo = { ...updatedInformations };
      delete updatedInfo.code_SIREN;
      setUpdatedInformations(updatedInfo);
    }

    if (isValidRcs(rcs)) {
      try {
        const response = await axios(`/rne/naf/${rcs}`);
        if (response) {
          const data = response.data.data;

          const raisonSociale =
            data.raison_sociale ||
            data.company_name ||
            data.denomination ||
            data.nom_entreprise ||
            "";

          setUser((prevUser) => ({
            ...prevUser,
            code_SIREN: rcs,
            code_NAF: data.naf_code || "",
            tv_intra: data.vat_number || "",
            raison_social: raisonSociale,
            user_type: "entreprise",
          }));

          setUpdatedInformations((prevInfo) => ({
            ...prevInfo,
            code_SIREN: rcs,
            code_NAF: data.naf_code || "",
            tv_intra: data.vat_number || "",
            raison_social: raisonSociale,
            user_type: "entreprise",
          }));

          if (user.user_type === "personne") {
            Swal.fire({
              title: "Changement de statut",
              text: "En ajoutant un numéro SIREN valide, votre compte sera considéré comme une entreprise.",
              icon: "info",
              confirmButtonColor: "#2A919D",
              customClass: {
                popup: 'swal2-popup-above-modal'
              }
            });
          }
        }
      } catch (error) {
        console.log("Invalid SIREN");
      }
    }
  };

  // Validation du téléphone
  const validatePhone = (value: string) => {
    const frenchPhoneRegex = /^(0\d{9}|\+33\d{9})$/;
    const europeanPhoneRegex = /^\+[1-9]\d{6,14}$/;
    const cleanValue = value.replace(/\s+/g, "");
    return (
      frenchPhoneRegex.test(cleanValue) || europeanPhoneRegex.test(cleanValue)
    );
  };

const handleSave = async () => {
  setShowTooltip(true);
  setLoading(true);
  try {
    const isEntreprise =
      updatedInformations.user_type === "entreprise" ||
      (user.user_type === "entreprise" && !updatedInformations.user_type);

    const dataToSend: any = {
      ...updatedInformations,
      id: user.id,
      profession: user.profession,
      ...(isEntreprise ? { is_entreprise: true } : { is_entreprise: false }),
    };

    if (user.profession === "Autre (à préciser)" || user.profession_is_other) {
      dataToSend.profession_other =
        updatedInformations.profession_other ?? user.profession_other ?? null;
      dataToSend.profession_is_other = true;
      if (dataToSend.profession_other) {
        dataToSend.profession = dataToSend.profession_other;
      }
    } else {
      dataToSend.profession_is_other = false;
    }

    await updateUserInfo(userId ?? parseInt(user.id), dataToSend);

    // ✅ Notification succès
    toast.success("Vos informations ont été mises à jour avec succès !");

    if (updatedInformations.name) {
      setUserName(updatedInformations.name);
      localStorage.setItem("userName", updatedInformations.name);
    }
    if (updatedInformations.firstname) {
      setUserFirstname(updatedInformations.firstname);
      localStorage.setItem("userFirstname", updatedInformations.firstname);
    }

    setUpdatedInformations({});
    setHasChanges(false);

    // Recharger les données
    const userData = await getUserDetails();
    if (userData) {
      const isOther = !!userData.profession_is_other;
      const formattedUser = {
        id: userData.id?.toString() || "",
        name: userData.name || "",
        firstname: userData.firstname || "",
        email: userData.email || "",
        reference: userData.reference || "",
        civility: userData.civility || "",
        code_parrainage: userData.code_parrainage || "",
        adresse: userData.adresse || "",
        number: userData.number || "",
        country: userData.country || "",
        postal_code: userData.postal_code || "",
        createdAt: userData.createdAt
          ? new Date(userData.createdAt).toLocaleDateString()
          : "",
        raison_social: userData.raison_social || "",
        code_SIREN: userData.code_SIREN || "",
        code_NAF: userData.code_NAF || "",
        tv_intra: userData.tv_intra || "",
        user_type: userData.user_type || "",
        status: userData.status || "",
        profession: isOther ? "Autre (à préciser)" : userData.profession || "",
        profession_other: userData.profession_other ?? "",
        profession_is_other: isOther,
        is_professional:
          userData.user_type === "professionnel" ||
          userData.user_type === "entreprise" ||
          false,
        bank_name: userData.bank_name || "",
        account_holder: userData.account_holder || "",
        account_number: userData.account_number || "",
        bic_swift: userData.bic_swift || "",
      };
      setUser(formattedUser);
      setInitialUser(formattedUser);
    }
  } catch (error: unknown) {
    let message = "";
    if (error instanceof AxiosError) {
      if (error.response) {
        message = error.response.data.message;
      } else if (error.request) {
        message = "Pas de réponse reçue : " + error.request;
      } else {
        message = "Erreur lors de la configuration de la requête : " + error.message;
      }
    } else {
      message = "Erreur inconnue : " + error;
    }
    // ❌ Remplacer Swal par toast erreur
    toast.error(message);
  }
  setLoading(false);
};


  // Gestion des changements de champs texte
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (showTooltip) return;

    const { id, value } = event.target;

    if (id === "number" && !validatePhone(value) && value.length > 0) {
      toast.error("Format de téléphone invalide");
      return;
    }

    setUser({ ...user, [id]: value });

    if (value !== initialUser[id as keyof typeof initialUser]) {
      setUpdatedInformations({ ...updatedInformations, [id]: value });
    } else {
      const updatedInfo = { ...updatedInformations };
      delete updatedInfo[id];
      setUpdatedInformations(updatedInfo);
    }
  };

  // Gestion de la catégorie de profession
  const handleProfessionCategoryChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    if (showTooltip) return;
    const category = event.target.value;
    setSelectedProfessionCategory(category);
    setUser({ ...user, profession: "" });
    const updatedInfo = { ...updatedInformations };
    delete updatedInfo.profession;
    setUpdatedInformations(updatedInfo);
  };

  // Gestion du changement de profession
  const handleProfessionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (showTooltip) return;

    const profession = event.target.value;

    if (profession === "Autre (à préciser)") {
      setUser((prev) => ({
        ...prev,
        profession,
        profession_is_other: true,
        profession_other: prev.profession_other ?? "",
      }));

      setUpdatedInformations((prev) => ({
        ...prev,
        profession,
        profession_is_other: true,
        ...(user.profession_other ? { profession_other: user.profession_other } : {}),
      }));
    } else {
      setUser((prev) => ({
        ...prev,
        profession,
        profession_is_other: false,
        profession_other: "",
      }));

      const updatedInfo = { ...updatedInformations };
      if (profession !== initialUser.profession) {
        updatedInfo.profession = profession;
      } else {
        delete updatedInfo.profession;
      }
      delete updatedInfo.profession_other;
      delete updatedInfo.profession_is_other;
      setUpdatedInformations(updatedInfo);
    }
  };

  // Gestion de la profession autre
  const handleOtherProfessionChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (showTooltip) return;

    const professionOther = event.target.value;
    setUser((prev) => ({ ...prev, profession_other: professionOther }));

    setUpdatedInformations((prev) => ({
      ...prev,
      profession_other: professionOther,
    }));
  };

  if (loading) {
    return <LoaderAnnonce />;
  }

  return (
    <div className="gestion-compte-admin-container" style={{ 
      padding: "20px",
      maxWidth: "100%",
      margin: "0 auto"
    }}>
      <ToastContainer />

      <div style={{ marginBottom: "30px" }}>

        <div
        className="gestion-compte-admin-header"
        style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "center",
        }}
        >
          <span style={{ color: "#666", fontSize: "14px" }}>
            Référence : <Tag color="blue"   style={{ marginLeft: 4 }}>{user.reference}</Tag>
          </span>
          <span style={{ color: "#666", fontSize: "14px" }}>
            Membre depuis : <Tag color="default"   style={{ marginLeft: 4 }}>{user.createdAt}</Tag>
          </span>
          {user.user_type && (
            <span style={{ color: "#666", fontSize: "14px" }}>
              Statut : 
              <Tag 
              style={{ marginLeft: 4 }}
              color={user.user_type === "entreprise" ? "#4bb0c0" : user.user_type === "professionnel" ? "#e2850b" : "#4bb0c0"}
              >
                {user.user_type === "entreprise" ? "Entreprise" : user.user_type === "professionnel" ? "Professionnel" : "Particulier"}
              </Tag>
            </span>
          )}
        </div>
      </div>

      {/* Section Informations personnelles */}
      <Card
        title={
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            <IdcardOutlined style={{ marginRight: "8px", color: "#2A919D" }} />
            Informations personnelles
          </span>
        }
        style={{ 
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
      >
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
          gap: "clamp(12px, 3vw, 20px)" 
        }}>
          <InputWithTooltip
            id="civility"
            label="Civilité"
            value={user.civility}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<UserOutlined />}
          />
          <InputWithTooltip
            id="firstname"
            label="Prénom"
            value={user.firstname}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<UserOutlined />}
          />
          <InputWithTooltip
            id="name"
            label="Nom"
            value={user.name}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<UserOutlined />}
          />
          <InputWithTooltip
            id="email"
            label="Email"
            value={user.email}
            onChange={handleInputChange}
            readOnly={true}
            tooltipMessage="L'email ne peut pas être modifié"
            icon={<MailOutlined />}
          />
          <InputWithTooltip
            id="number"
            label="Téléphone"
            value={user.number}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<PhoneOutlined />}
          />
        </div>
      </Card>

      {/* Section Adresse */}
      <Card
        title={
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            <HomeOutlined style={{ marginRight: "8px", color: "#2A919D" }} />
            Adresse
          </span>
        }
        style={{ 
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
      >
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
          gap: "clamp(12px, 3vw, 20px)" 
        }}>
          <InputWithTooltip
            id="adresse"
            label="Adresse"
            value={user.adresse}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<HomeOutlined />}
          />
          <InputWithTooltip
            id="postal_code"
            label="Code Postal"
            value={user.postal_code}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<HomeOutlined />}
          />
          <InputWithTooltip
            id="country"
            label="Ville"
            value={user.country}
            onChange={handleInputChange}
            readOnly={showTooltip}
            tooltipMessage="Cliquez sur modifier pour changer"
            icon={<GlobalOutlined />}
          />
        </div>
      </Card>

      {/* Section Informations Entreprise */}
      {(user.user_type === "entreprise" || user.is_professional) && (
        <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              <BuildOutlined style={{ marginRight: "8px", color: "#2A919D" }} />
              Informations entreprise
            </span>
          }
          style={{ 
            marginBottom: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
          }}
        >
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
            gap: "clamp(12px, 3vw, 20px)" 
          }}>
            <InputWithTooltip
              id="raison_social"
              label="Raison sociale"
              value={user.raison_social}
              onChange={handleInputChange}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<BuildOutlined />}
            />
            <InputWithTooltip
              id="code_SIREN"
              label="Numéro SIREN"
              value={user.code_SIREN}
              onChange={handleRcsInput}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<IdcardOutlined />}
            />
            <InputWithTooltip
              id="code_NAF"
              label="Code NAF"
              value={user.code_NAF}
              onChange={handleInputChange}
              readOnly={true}
              tooltipMessage="Code NAF détecté automatiquement"
              icon={<IdcardOutlined />}
            />
            <InputWithTooltip
              id="tv_intra"
              label="Numéro de TVA intracommunautaire"
              value={user.tv_intra}
              onChange={handleInputChange}
              readOnly={true}
              tooltipMessage="TVA détectée automatiquement"
              icon={<IdcardOutlined />}
            />
          </div>
        </Card>
      )}

      {/* Section Profession */}
      <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              <UserOutlined style={{ marginRight: "8px", color: "#2A919D" }} />
              Profession
            </span>
          }
          style={{ 
            marginBottom: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
          }}
        >
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
            gap: "clamp(12px, 3vw, 20px)" 
          }}>
            <div>
              <label className="form-label" style={{ 
                fontWeight: 500, 
                marginBottom: "6px",
                color: "#555",
                fontSize: "13px"
              }}>
                Catégorie
              </label>
              <select
                id="profession_category"
                className="form-control"
                value={selectedProfessionCategory}
                onChange={handleProfessionCategoryChange}
                disabled={showTooltip}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: showTooltip ? "1px solid #e0e0e0" : "1px solid #d9d9d9",
                  backgroundColor: showTooltip ? "#fafafa" : "white",
                  fontSize: "14px",
                  color: showTooltip ? "#666" : "#333"
                }}
              >
                <option value="">Sélectionner une catégorie</option>
                {Object.keys(PROFESSION_OPTIONS).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {selectedProfessionCategory && (
              <div>
                <label className="form-label" style={{ 
                  fontWeight: 500, 
                  marginBottom: "6px",
                  color: "#555",
                  fontSize: "13px"
                }}>
                  Profession
                </label>
                <select
                  id="profession"
                  className="form-control"
                  value={user.profession}
                  onChange={handleProfessionChange}
                  disabled={showTooltip}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: showTooltip ? "1px solid #e0e0e0" : "1px solid #d9d9d9",
                    backgroundColor: showTooltip ? "#fafafa" : "white",
                    fontSize: "14px",
                    color: showTooltip ? "#666" : "#333"
                  }}
                >
                  <option value="">Sélectionner une profession</option>
                  {PROFESSION_OPTIONS[selectedProfessionCategory]?.map(
                    (prof) => (
                      <option key={prof} value={prof}>
                        {prof}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            {user.profession === "Autre (à préciser)" && (
              <div>
                <label className="form-label" style={{ 
                  fontWeight: 500, 
                  marginBottom: "6px",
                  color: "#555",
                  fontSize: "13px"
                }}>
                  Préciser votre profession
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={user.profession_other || ""}
                  onChange={handleOtherProfessionChange}
                  placeholder="Entrez votre profession"
                  disabled={showTooltip}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: showTooltip ? "1px solid #e0e0e0" : "1px solid #d9d9d9",
                    backgroundColor: showTooltip ? "#fafafa" : "white",
                    fontSize: "14px",
                    color: showTooltip ? "#666" : "#333"
                  }}
                />
              </div>
            )}
          </div>
        </Card>

      {/* Section Coordonnées Bancaires */}
      {user.is_professional && (
        <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              <BankOutlined style={{ marginRight: "8px", color: "#2A919D" }} />
              Coordonnées bancaires
            </span>
          }
          style={{ 
            marginBottom: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
          }}
        >
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
            gap: "clamp(12px, 3vw, 20px)" 
          }}>
            <InputWithTooltip
              id="bank_name"
              label="Nom de la banque"
              value={user.bank_name}
              onChange={handleInputChange}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<BankOutlined />}
            />
            <InputWithTooltip
              id="account_holder"
              label="Titulaire du compte"
              value={user.account_holder}
              onChange={handleInputChange}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<UserOutlined />}
            />
            <InputWithTooltip
              id="account_number"
              label="IBAN"
              value={user.account_number}
              onChange={handleInputChange}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<BankOutlined />}
            />
            <InputWithTooltip
              id="bic_swift"
              label="BIC / SWIFT"
              value={user.bic_swift}
              onChange={handleInputChange}
              readOnly={showTooltip}
              tooltipMessage="Cliquez sur modifier pour changer"
              icon={<BankOutlined />}
            />
          </div>

          <div style={{ marginTop: "20px" }}>
            <Button
              icon={<EditOutlined />}
              onClick={() => setRibModalVisible(true)}
              disabled={showTooltip}
              style={{ 
                backgroundColor: "#2A919D", 
                color: "white",
                border: "none",
                padding: "8px 20px",
                height: "auto"
              }}
            >
              Ajouter RIB scanné
            </Button>
          </div>
        </Card>
      )}

      <Divider />

      {/* Boutons d'action */}
        <div
        className="actions-footer"
        style={{
            display: "flex",
            gap: "12px",
            marginTop: "30px",
            justifyContent: "flex-end",
            flexWrap: "wrap",
        }}
        >
        {showTooltip ? (
          <Button
            onClick={handleEditClick}
            icon={<EditOutlined />}
            style={{
              padding: "10px 25px",
              fontSize: "14px",
              backgroundColor: "#2A919D",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              height: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            Modifier
          </Button>
        ) : (
          <Space>
            <Button
              onClick={handleCancel}
              icon={<CloseOutlined />}
              style={{
                padding: "10px 25px",
                fontSize: "14px",
                backgroundColor: "white",
                color: "#666",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                cursor: "pointer",
                height: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges}
              icon={<SaveOutlined />}
              style={{
                padding: "10px 25px",
                fontSize: "14px",
                backgroundColor: hasChanges ? "#4bb0c0" : "#f18445",
                color: "white !important",
                border: "none",
                borderRadius: "6px",
                cursor: hasChanges ? "pointer" : "not-allowed",
                height: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              Enregistrer
            </Button>
          </Space>
        )}
      </div>

      {/* Modal RIB */}
      <RibModal
        visible={ribModalVisible}
        onCancel={() => setRibModalVisible(false)}
        onConfirm={async (rib: string, bic?: string) => {
          if (rib) {
            setUser((prev) => ({
              ...prev,
              iban: rib,
              ...(bic ? { bic_swift: bic } : {}),
            }));
            setUpdatedInformations((prev) => ({
              ...prev,
              iban: rib,
              ...(bic ? { bic_swift: bic } : {}),
            }));
            setRibModalVisible(false);
            toast.success("RIB mis à jour avec succès");
          }
        }}
      />
    </div>
  );
}

export default GestionCompteAdmin;